import type { ReactNode } from 'react';
import { useDungeonStore } from '@/state/dungeonStore';
import { TopHud } from './TopHud';
import { BottomNav, type Tab } from './BottomNav';
import { AchievementToasts } from '@/ui/components/AchievementToast';
import { TutorialOverlay } from '@/ui/components/TutorialOverlay';
import './AppShell.css';

interface AppShellProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  children: ReactNode;
}

/**
 * Hides the TopHud + BottomNav while a combat run is active. Two reasons:
 * 1. Stacking — the bottom nav (z-2 sibling) was painting over the combat HUD's
 *    button row (special / siphon / vault verlassen). Hiding solves that
 *    cleaner than fighting z-index across stacking contexts.
 * 2. UX — during a fight the player should be focused, not seeing tab buttons
 *    that would silently abandon the run on tap.
 */
export function AppShell({ activeTab, onTabChange, children }: AppShellProps) {
  const inCombat = useDungeonStore((s) => s.phase === 'combat');
  return (
    <div className="app-shell">
      {!inCombat && <TopHud />}
      <main className="app-shell__content">{children}</main>
      {!inCombat && <BottomNav active={activeTab} onChange={onTabChange} />}
      {/* Achievement toasts persist across phases so an unlock during combat
          (e.g. "first_boss" right after killing) still surfaces visibly. */}
      <AchievementToasts />
      {/* FTUE overlay — only renders when player.tutorialStep < 99. Pointer
          events are scoped to the card itself so gameplay underneath is
          unaffected. */}
      <TutorialOverlay />
    </div>
  );
}
